import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DocumentService } from 'src/app/services/document.service';
import {
  DocumentMetadata,
  MetadataType,
  SSLCertificateInfo,
} from '../../models/documentMetadata';

import moment from 'moment';

@Component({
  selector: 'app-crawled-document-metadata',
  templateUrl: './crawled-document-metadata.component.html',
  styleUrl: './crawled-document-metadata.component.scss',
})
export class CrawledDocumentMetadataComponent {
  readonly id = input<string>();
  readonly title = input<boolean>(true);

  protected readonly displayLimit = 3;
  readonly limit = signal<number>(this.displayLimit);
  readonly crawledDocumentMetadata = signal<DocumentMetadata[]>([]);

  readonly showToggle = computed(
    () => this.crawledDocumentMetadata().length > this.displayLimit
  );
  readonly isExpanded = computed(
    () => this.limit() > this.displayLimit
  );

  protected readonly MetadataType: typeof MetadataType = MetadataType;

  private readonly documentService = inject(DocumentService);
  private readonly translate = inject(TranslateService);
  private readonly toastr = inject(ToastrService);

  readonly sslCertByMetaId = computed(() =>
    Object.fromEntries(
      this.crawledDocumentMetadata()
        .filter(m => m.type === MetadataType.CRAWLER_SSL_CERTIFICATE)
        .map(m => [m._id, this.parseSslCertificate(m.value)])
    )
  );

  constructor() {
    moment.locale(this.translate.getDefaultLang());

    effect(() => {
      const id = this.id();
      if (id) {
        this.retrieveMetadata(id);
      }
    });
  }

  private async retrieveMetadata(id: string) {
    try {
      this.crawledDocumentMetadata.set(
        await this.documentService.retrieveCrawlerMetadata(id),
      );
    } catch (err) {
      console.error(err);
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  toggle(): void {
    this.limit.set(
      this.isExpanded()
        ? this.displayLimit
        : this.crawledDocumentMetadata().length,
    );
  }

  // region Utils

  toReadableDate(dateStr: string): string {
    if (!dateStr) return this.translate.instant('ERRORS.NOT_AVAILABLE');

    const date = moment(dateStr);
    if (!date.isValid()) return this.translate.instant('ERRORS.INVALID_DATE');

    const now = moment();
    const daysDiff = Math.abs(date.diff(now, 'days'));

    // relative for dates within 7 days (past or future)
    if (daysDiff <= 7) {
      return date.fromNow(); // '2 days ago', 'in 3 days', etc.
    }

    return date.format('LLLL');
  }

  toISODate(dateStr: string): string {
    return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
  }

  parseSslCertificate(value: string): SSLCertificateInfo | null {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  // endregion utils
}
